import React, { useMemo } from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from 'recharts'
import { cn } from '@/lib/utils'
import { paceBand } from '@/lib/plain-language'
import type { BookingCurveData } from '@/types/calendar'

interface BookingCurveChartProps {
  data: BookingCurveData
  currentLeadTime: number
}

const PACE_TEXT_TONE = {
  positive: 'text-emerald-600',
  neutral: 'text-muted-foreground',
  negative: 'text-rose-600',
} as const

const CurveTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null
  const point = payload[0]?.payload
  if (!point) return null

  const actual = point.actual
  const baseline = point.baseline
  const ratio = actual != null && baseline != null && baseline > 0 ? actual / baseline : null
  const band = ratio != null ? paceBand(ratio) : null

  return (
    <div className="rounded-lg border border-border/60 bg-white px-3 py-2 shadow-lg shadow-black/5">
      <p className="text-[11px] font-medium text-muted-foreground">
        {point.leadTimeDays} days out
      </p>
      {actual != null && (
        <p className="text-sm font-semibold text-foreground">{actual}% full</p>
      )}
      {baseline != null && (
        <p className="text-xs text-muted-foreground">usually ~{baseline}% by now</p>
      )}
      {band && (
        <p className={cn('text-xs font-semibold mt-0.5', PACE_TEXT_TONE[band.tone])}>
          {band.label}
        </p>
      )}
    </div>
  )
}

export default function BookingCurveChart({ data, currentLeadTime }: BookingCurveChartProps) {
  const chartData = useMemo(() => {
    const map = new Map<number, { leadTimeDays: number; actual?: number; baseline?: number }>()

    for (const pt of data.actual) {
      map.set(pt.leadTimeDays, { leadTimeDays: pt.leadTimeDays, actual: pt.occupancyPct })
    }
    for (const pt of data.baseline) {
      const existing = map.get(pt.leadTimeDays)
      if (existing) {
        existing.baseline = pt.expectedOccPct
      } else {
        map.set(pt.leadTimeDays, { leadTimeDays: pt.leadTimeDays, baseline: pt.expectedOccPct })
      }
    }

    return Array.from(map.values()).sort((a, b) => b.leadTimeDays - a.leadTimeDays)
  }, [data])

  if (data.actual.length < 2) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-white p-8 text-center shadow-sm">
        <p className="text-sm font-medium text-foreground">Not enough booking history</p>
        <p className="mt-1 text-xs text-muted-foreground">
          The booking curve for this date will appear once more snapshots accumulate.
        </p>
      </div>
    )
  }

  const { primarySource, maxSampleSize } = data.confidence
  const stays = `${maxSampleSize} past stay${maxSampleSize === 1 ? '' : 's'}`
  const confidenceText = primarySource === 'historical'
    ? `Based on ${stays}`
    : primarySource === 'blended'
    ? `Blended with industry data (${stays})`
    : 'Based on industry defaults (limited history)'

  return (
    <div>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="curveActualGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#0d9488" stopOpacity={0.12} />
              <stop offset="95%" stopColor="#0d9488" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis
            dataKey="leadTimeDays"
            reversed
            tick={{ fontSize: 10, fill: '#64748b' }}
            tickLine={false}
            axisLine={{ stroke: '#e2e8f0' }}
            tickFormatter={(v: number) => `${v}d`}
            label={{ value: 'Days before arrival', position: 'insideBottom', offset: -2, fontSize: 10, fill: '#94a3b8' }}
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: number) => `${v}%`}
            width={40}
          />
          <Tooltip content={<CurveTooltip />} />
          {currentLeadTime > 0 && (
            <ReferenceLine
              x={currentLeadTime}
              stroke="#0d9488"
              strokeDasharray="3 3"
              strokeWidth={1.5}
              label={{
                value: 'Now',
                position: 'top',
                fontSize: 10,
                fill: '#0d9488',
                fontWeight: 600,
              }}
            />
          )}
          <Area
            type="monotone"
            dataKey="baseline"
            stroke="#94a3b8"
            strokeDasharray="4 3"
            strokeWidth={1.5}
            fill="none"
            dot={false}
            connectNulls
          />
          <Area
            type="monotone"
            dataKey="actual"
            stroke="#0d9488"
            strokeWidth={2}
            fill="url(#curveActualGrad)"
            dot={false}
            connectNulls
          />
        </AreaChart>
      </ResponsiveContainer>
      <p className="mt-2 text-[11px] text-muted-foreground">{confidenceText}</p>
    </div>
  )
}
